import React, { useState } from 'react';
import { HiOutlineX, HiOutlineCheckCircle } from 'react-icons/hi';
import toast from 'react-hot-toast';
import { salesPipelineAPI } from '../../services/api';
import { getReleasedBy, formatRole, formatDateTime } from '../../utils/salesOrderRelease';

/**
 * Admin approval for a Sales Order. Admin must choose Credit or Advance; only then
 * is the order released to the Store Assembly queue.
 */
const ApproveSalesOrderModal = ({ order, onClose, onApproved }) => {
  const [paymentType, setPaymentType] = useState(order?.paymentType || '');
  const [creditDays, setCreditDays] = useState(order?.creditDays ?? 30);
  const [advanceAmount, setAdvanceAmount] = useState(order?.advanceAmount || '');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  if (!order) return null;
  const released = getReleasedBy(order);
  const grandTotal = order.grandTotal || order.totalAmount || 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!paymentType) {
      toast.error('Select Credit or Advance');
      return;
    }
    try {
      setSaving(true);
      await salesPipelineAPI.approve(order._id, {
        paymentType,
        creditDays: paymentType === 'credit' ? Number(creditDays) || 0 : undefined,
        advanceAmount: paymentType === 'advance' ? Number(advanceAmount) || 0 : undefined,
        note
      });
      toast.success(`✅ ${order.salesOrderNumber || 'Sales Order'} approved and sent to Store`);
      onApproved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to approve Sales Order');
    } finally {
      setSaving(false);
    }
  };

  const optionStyle = (active) => ({
    flex: 1, padding: '12px', borderRadius: '10px', cursor: 'pointer', textAlign: 'left',
    border: `2px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
    background: active ? 'rgba(59,130,246,0.08)' : 'transparent', color: 'var(--text-primary)'
  });

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px'
    }} onClick={e => e.target === e.currentTarget && !saving && onClose()}>
      <div style={{
        background: 'var(--bg-card)', borderRadius: '16px', width: '100%', maxWidth: '520px', maxHeight: '90vh',
        border: '1px solid var(--border)', overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
        display: 'flex', flexDirection: 'column'
      }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>Approve {order.salesOrderNumber || 'Sales Order'}</h3>
          <button type="button" onClick={onClose} disabled={saving} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: '1.2rem' }}><HiOutlineX /></button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '20px 24px', overflowY: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', fontSize: '0.85rem', marginBottom: '18px' }}>
            <div>
              <div style={{ color: 'var(--text-dim)', fontSize: '0.75rem' }}>Customer</div>
              <div style={{ fontWeight: 700 }}>{order.customerName}</div>
            </div>
            <div>
              <div style={{ color: 'var(--text-dim)', fontSize: '0.75rem' }}>Order Value</div>
              <div style={{ fontWeight: 700 }}>₹{grandTotal.toLocaleString('en-IN')}</div>
            </div>
            <div>
              <div style={{ color: 'var(--text-dim)', fontSize: '0.75rem' }}>Released By</div>
              <div style={{ fontWeight: 700 }}>{released.name || '—'}{released.role ? ` (${formatRole(released.role)})` : ''}</div>
              {released.at && <div style={{ color: 'var(--text-dim)', fontSize: '0.75rem' }}>{formatDateTime(released.at)}</div>}
            </div>
            <div>
              <div style={{ color: 'var(--text-dim)', fontSize: '0.75rem' }}>Terms proposed by Sales</div>
              <div style={{ fontWeight: 600 }}>{order.paymentTerms || '—'}{order.creditDays ? ` · ${order.creditDays} days` : ''}</div>
            </div>
          </div>

          <div className="form-label" style={{ marginBottom: '8px' }}>Payment basis *</div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
            <button type="button" style={optionStyle(paymentType === 'credit')} onClick={() => setPaymentType('credit')}>
              <div style={{ fontWeight: 700 }}>Credit</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Goods first, payment within credit days</div>
            </button>
            <button type="button" style={optionStyle(paymentType === 'advance')} onClick={() => setPaymentType('advance')}>
              <div style={{ fontWeight: 700 }}>Advance</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Payment received before dispatch</div>
            </button>
          </div>

          {paymentType === 'credit' && (
            <div className="form-group">
              <label className="form-label">Credit days</label>
              <input className="form-input" type="number" min="0" value={creditDays} onChange={e => setCreditDays(e.target.value)} />
            </div>
          )}
          {paymentType === 'advance' && (
            <div className="form-group">
              <label className="form-label">Advance amount (₹)</label>
              <input className="form-input" type="number" min="0" placeholder={`Order value ₹${grandTotal.toLocaleString('en-IN')}`} value={advanceAmount} onChange={e => setAdvanceAmount(e.target.value)} />
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Note for Store / Sales (optional)</label>
            <textarea className="form-input" rows={2} value={note} onChange={e => setNote(e.target.value)} />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '6px' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving || !paymentType} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <HiOutlineCheckCircle /> {saving ? 'Approving…' : 'Approve & Send to Store'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApproveSalesOrderModal;
